import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { GammaExposurePoint, GammaMetricKey } from '../types/StockData';

interface GammaChartProps {
  data: GammaExposurePoint[];
  selectedMetric: GammaMetricKey;
  currentPrice: number;
}

const GammaChart: React.FC<GammaChartProps> = ({ data, selectedMetric, currentPrice }) => {
  // Format data for the chart and sort by strike price
  const chartData = data
    .map(point => ({
      strike: point.strike,
      value: point[selectedMetric],
      isAtm: Math.abs(point.strike - currentPrice) < 2.5, // Highlight strikes near current price
    }))
    .sort((a, b) => a.strike - b.strike); // Sort strikes in ascending order

  // Custom tooltip formatter
  const formatTooltip = (value: number, name: string, props: any) => {
    const formattedValue = typeof value === 'number' ? value.toLocaleString() : value;
    return [formattedValue, getMetricDisplayName(selectedMetric)];
  };

  // Get display name for metric
  const getMetricDisplayName = (metric: GammaMetricKey): string => {
    switch (metric) {
      case 'netGamma': return 'Net Gamma';
      case 'netCallGamma': return 'Net Call Gamma';
      case 'netPutGamma': return 'Net Put Gamma';
      case 'netDelta': return 'Net Delta';
      case 'netVanna': return 'Net Vanna';
      default: return metric;
    }
  };

  // Custom bar color based on value
  const getBarColor = (value: number) => {
    if (value > 0) return '#10b981'; // Green for positive
    if (value < 0) return '#ef4444'; // Red for negative
    return '#6b7280'; // Gray for zero
  };

  // Calculate Y-axis domain for better scaling
  const values = chartData.map(d => d.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  
  // Use tighter scaling - only 5% padding and ensure we don't include zero unless data crosses zero
  const range = maxValue - minValue;
  const padding = range * 0.05; // 5% padding
  
  let yAxisDomain;
  if (minValue >= 0) {
    // All positive values - start from slightly below minimum
    yAxisDomain = [Math.max(0, minValue - padding), maxValue + padding];
  } else if (maxValue <= 0) {
    // All negative values - end slightly above maximum
    yAxisDomain = [minValue - padding, Math.min(0, maxValue + padding)];
  } else {
    // Values cross zero - include zero with padding
    yAxisDomain = [minValue - padding, maxValue + padding];
  }

  return (
    <div className="w-full h-[700px] bg-white rounded-lg shadow-lg p-6 mb-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          {getMetricDisplayName(selectedMetric)} by Strike
        </h3>
        <p className="text-sm text-gray-600">
          Current Price: ${currentPrice.toFixed(2)}
        </p>
      </div>
      
      <ResponsiveContainer width="100%" height="88%">
        <BarChart
          data={chartData}
          margin={{
            top: 30,
            right: 40,
            left: 60,
            bottom: 60,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey="strike" 
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={(value) => `$${value}`}
          />
          <YAxis 
            stroke="#6b7280"
            fontSize={12}
            domain={yAxisDomain}
            tickFormatter={(value) => {
              if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
              if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
              if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
              return value.toFixed(0);
            }}
          />
          <Tooltip 
            formatter={formatTooltip}
            labelFormatter={(label) => `Strike: $${label}`}
            contentStyle={{
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '12px'
            }}
          />
          <Bar 
            dataKey="value" 
            stroke="#374151"
            strokeWidth={0.5}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry.value)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default GammaChart;
